import { MouseEventHandler, useCallback, useState } from 'react'
import { useFocusWithin } from 'react-aria'
import { ArrowDownwardIcon } from '~/components/icons/generated/ArrowDownwardIcon'
import { useIsMobile } from '~/hooks/useMediaQuery'

type Props<T extends string> = {
  onChange?: (selected: T) => void
  value: T
  values: Set<T>
}

const Option = <T extends string>({
  children,
  selected,
  onClick,
}: {
  children: T
  selected: boolean
  onClick?: (value: T) => void
}) => {
  const onOptionClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => onClick?.(children),
    [children, onClick]
  )
  return (
    <button
      role="option"
      disabled={selected}
      aria-selected={selected}
      onClick={onOptionClick}
      className="flex size-full items-center justify-center bg-white text-gray-600 aria-selected:bg-black aria-selected:text-white xl:justify-start xl:px-5 xl:text-[13px] xl:hover:bg-gray-60 xl:aria-selected:bg-gray-200 xl:aria-selected:text-gray-600"
      type="button"
    >
      {children}
    </button>
  )
}

export const FilterSelector = <T extends string>({
  onChange,
  value,
  values,
}: Props<T>) => {
  const [openingOptions, showOptions] = useState(false)
  const onSelectorClick = useCallback(() => showOptions((prev) => !prev), [])
  const onOptionClick = useCallback(
    (v: T) => {
      showOptions(false)
      onChange?.(v)
    },
    [onChange]
  )
  const isMobile = useIsMobile()
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      if (!isMobile) showOptions(true)
    },
    onBlurWithin: () => {
      if (!isMobile) showOptions(false)
    },
    onFocusWithinChange: (isFocusWithin) => showOptions(isFocusWithin),
  })

  const spLayoutClassName = `
    w-[145px] divide-y 
    [&>li]:border-gray-100
    before:absolute before:left-1/2 before:top-0 before:-translate-x-1/2 before:-translate-y-full before:border-[7px] before:border-transparent before:border-b-gray-300 
    after:absolute after:left-1/2 after:top-0 after:-translate-x-1/2 after:-translate-y-full after:border-[6px] after:border-transparent after:border-b-white 
    aria-hidden:pointer-events-none aria-hidden:translate-y-[-10px] aria-hidden:opacity-0 
  `

  const pcLayoutClassName = `
    xl:right-0 xl:w-[134px] xl:divide-none xl:bg-white xl:pb-3 xl:pt-[14px]
    xl:before:hidden 
    xl:after:hidden 
  `
  return (
    <div
      className="relative inline-block"
      {...focusWithinProps}
      // focusWithinPropsにonFocusが含まれているため
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      onMouseOver={() => {
        if (!isMobile) showOptions(true)
      }}
      onMouseLeave={() => {
        if (!isMobile) showOptions(false)
      }}
    >
      <span className="xl:hidden">表示：</span>
      <button
        className="inline-flex items-center space-x-[6px] pl-[3px] font-bold xl:gap-[5px] xl:p-[10px] xl:pt-2"
        onClick={onSelectorClick}
        type="button"
      >
        <span className="leading-[1.2] xl:text-[14px] xl:font-normal">
          {value}
        </span>
        <ArrowDownwardIcon
          className={`h-3 xl:h-[14px] ${openingOptions && isMobile ? 'rotate-180' : ''}`}
        />
      </button>
      <ul
        aria-hidden={!openingOptions}
        inert={!openingOptions}
        className={`absolute top-8 z-10 rounded-[3px] opacity-100 shadow-[0_1px_4px_rgba(0,0,0,0.3)] transition-[opacity,transform] duration-100 ease-out ${spLayoutClassName} ${pcLayoutClassName}`}
      >
        {Array.from(values).map((v) => (
          <li
            key={v}
            className="h-[47px] overflow-hidden first:rounded-t last:rounded-b xl:h-8 xl:first:rounded-none xl:last:rounded-none"
          >
            <Option onClick={onOptionClick} selected={v === value}>
              {v}
            </Option>
          </li>
        ))}
      </ul>
    </div>
  )
}
